import { alpha } from '@mui/material/styles';
import { getSharedDataGridStyles } from '../styles/sharedStyles';
import { ICustomTheme } from '../types/ICustomTheme';

export const getMenuStyles = (themeOptions: ICustomTheme) => ({
     styleOverrides: {
          root: {
               '& .MuiPaper-root': {
                    backgroundColor: themeOptions.palette.background.paper,
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                    borderRadius: themeOptions.shape.borderRadius,

                    // Animação de entrada para menus
                    animation: 'menuFadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
               },

               // Keyframes para menu
               '@keyframes menuFadeIn': {
                    '0%': {
                         opacity: 0,
                         transform: 'scale(0.95) translateY(-5px)',
                    },
                    '100%': {
                         opacity: 1,
                         transform: 'scale(1) translateY(0)',
                    },
               },

               // Keyframes para items do menu
               '@keyframes menuItemSlideIn': {
                    '0%': {
                         opacity: 0,
                         transform: 'translateX(-10px)',
                    },
                    '100%': {
                         opacity: 1,
                         transform: 'translateX(0)',
                    },
               },
          },
          paper: {
               '& .MuiMenuItem-root': {
                    color: themeOptions.palette.text.primary,
                    transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, transform 0.2s ease-in-out',
                    position: 'relative',
                    overflow: 'hidden',

                    // Animação de entrada em cascata para itens do menu
                    '&:nth-of-type(1)': { animationDelay: '0.05s' },
                    '&:nth-of-type(2)': { animationDelay: '0.1s' },
                    '&:nth-of-type(3)': { animationDelay: '0.15s' },
                    '&:nth-of-type(4)': { animationDelay: '0.2s' },
                    '&:nth-of-type(5)': { animationDelay: '0.25s' },
                    animation: 'menuItemSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) both',
                    '&:hover': {
                         backgroundColor: alpha(themeOptions.palette.text.primary, 0.08),
                    },
                    '&.Mui-selected': {
                         backgroundColor: alpha(themeOptions.palette.text.primary, 0.12),
                         '&:hover': {
                              backgroundColor: alpha(themeOptions.palette.text.primary, 0.16),
                         },
                    },
               },
               // Estilização dos campos do filtro
               '& .MuiFormControl-root': {
                    margin: '8px',
                    width: '100%',
                    '& .MuiInputBase-root': {
                         color: themeOptions.palette.text.primary,
                         backgroundColor: alpha(themeOptions.palette.background.default, 0.5),
                         '&:hover': {
                              backgroundColor: alpha(themeOptions.palette.background.default, 0.7),
                         },
                         '&.Mui-focused': {
                              backgroundColor: alpha(themeOptions.palette.background.default, 0.9),
                         },
                    },
                    '& .MuiSelect-select': {
                         padding: '8px 14px',
                    },
                    '& .MuiInputLabel-root': {
                         color: themeOptions.palette.text.secondary,
                         '&.Mui-focused': {
                              color: themeOptions.palette.text.primary,
                         },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                         borderColor: alpha(themeOptions.palette.divider, 0.8),
                    },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                         borderColor: themeOptions.palette.text.primary,
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                         borderColor: themeOptions.palette.text.primary,
                    },
               },
          },
     },
});

export const getDrawerStyles = (themeOptions: ICustomTheme) => ({
     styleOverrides: {
          root: {
               '& .MuiDrawer-paper': {
                    backgroundColor: themeOptions.palette.background.default,
                    color: themeOptions.palette.text.primary,
                    borderRight: `1px solid ${themeOptions.palette.divider}`,
               },
          },
          paper: {
               '& .MuiListItem-root': {
                    borderRadius: themeOptions.shape.borderRadius,
                    margin: '4px 8px',
                    transition: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',

                    // Animação de entrada em cascata
                    '&:nth-of-type(1)': { animationDelay: '0.1s' },
                    '&:nth-of-type(2)': { animationDelay: '0.15s' },
                    '&:nth-of-type(3)': { animationDelay: '0.2s' },
                    '&:nth-of-type(4)': { animationDelay: '0.25s' },
                    '&:nth-of-type(5)': { animationDelay: '0.3s' },
                    animation: 'drawerItemSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) both',
                    '&:hover': {
                         backgroundColor: alpha(themeOptions.palette.text.primary, 0.08),
                         transform: 'translateX(4px)',
                    },
                    '&.Mui-selected': {
                         backgroundColor: themeOptions.palette.divider,
                         '&:hover': {
                              backgroundColor: alpha(themeOptions.palette.text.primary, 0.12),
                         },
                    },
               },
               '& .MuiListItemIcon-root': {
                    minWidth: 40,
                    color: themeOptions.palette.text.secondary,
               },
               '& .MuiCollapse-root': {
                    '& .MuiListItem-root': {
                         paddingLeft: '32px',
                    },
               },
               '& .MuiDivider-root': {
                    margin: '8px 0',
                    borderColor: themeOptions.palette.divider,
               },

               // Keyframes para itens do drawer
               '@keyframes drawerItemSlideIn': {
                    '0%': {
                         opacity: 0,
                         transform: 'translateX(-20px)',
                    },
                    '100%': {
                         opacity: 1,
                         transform: 'translateX(0)',
                    },
               },
          },
     },
});

export const getDataGridStyles = (themeOptions: ICustomTheme) => ({
     styleOverrides: {
          root: getSharedDataGridStyles(themeOptions),
     },
     defaultProps: {
          density: 'compact',
     },
});

export const getDataGridProStyles = (themeOptions: ICustomTheme) => ({
     styleOverrides: {
          root: getSharedDataGridStyles(themeOptions),
     },
     defaultProps: {
          density: 'compact',
     },
});
